/**
 * Shared chrome for the annotation flow page: the breadcrumb at the top
 * and the full-bleed background container used by every full-screen
 * state (loading, error, terminal-record).
 */
import React from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../../../../ui/button';


export const FullScreen: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex h-screen flex-col items-center justify-center bg-[var(--go-spring)] px-4">
    {children}
  </div>
);


/**
 * Top-of-page breadcrumb. Mirrors the Monitor (`/v4/records`) inline
 * pattern: parent link → "/" separator → current page label (full record
 * id, in monospace, with `aria-current="page"`).
 */
export const FlowHeaderBreadcrumb: React.FC<{ recordId: string }> = ({
  recordId,
}) => (
  <div className="flex items-center gap-1.5 border-b border-border bg-background px-4 py-3 text-xs text-muted-foreground md:px-6">
    <Link
      to="/v4/go-lab/annotate/queue"
      className="font-medium text-primary hover:underline"
    >
      Annotation
    </Link>
    <span aria-hidden>/</span>
    <span className="font-mono text-[12px]" aria-current="page">
      {recordId}
    </span>
  </div>
);


export const LoadingScreen: React.FC = () => (
  <FullScreen>
    <div
      className="h-12 w-12 animate-pulse rounded-full bg-muted"
      data-testid="annotation-loading"
    />
    <p className="mt-3 text-[13px] text-muted-foreground">Loading record…</p>
  </FullScreen>
);


export const ErrorScreen: React.FC<{
  message: string;
  onBack: () => void;
}> = ({ message, onBack }) => (
  <FullScreen>
    <p
      className="mb-4 text-center text-[14px] text-muted-foreground"
      data-testid="annotation-error"
    >
      {message}
    </p>
    <Button variant="link" onClick={onBack}>
      Back to queue
    </Button>
  </FullScreen>
);
